import React, {
  useEffect,
  useState,
  useCallback
} from "react";
import useUsuarios from "../../hooks/useUsuarios";
import { formatarNomeUpper } from "../../utils/formatadores";

function GerenciamentoUsuarios() {
  const {
    usuarios,
    carregarUsuarios,
    atualizarUsuario,
    resetSenha
  } = useUsuarios();

  const [usuarioEditando, setUsuarioEditando] =
    useState(null);

  const [form, setForm] = useState({
    nome: "",
    email: "",
    profissao: "",
    vinculo: "",
    role: "",
    isAdmin: false,
    isTI: false,
    statusUsuario: "desabilitado"
  });

  const [novaSenha, setNovaSenha] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [debouncedSearch, setDebouncedSearch] =
    useState("");

  const [page, setPage] =
    useState(1);

  const [totalPages, setTotalPages] =
    useState(1);

  const limit = 20;

  // =========================================================
  // DEBOUNCE
  // =========================================================

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 750);

    return () => clearTimeout(timer);
  }, [search]);

  // =========================================================
  // CARREGAR USUÁRIOS
  // =========================================================

  const fetchUsuarios = useCallback(async () => {
  try {
    const data = await carregarUsuarios(
      debouncedSearch,
      page,
      limit
    );

    setTotalPages(
      data.totalPages || 1
    );
  } catch (err) {
    if (err.response) {
      console.error(
        "Erro da API:",
        err.response.data
      );
    } else if (err.request) {
      console.error(
        "Servidor não respondeu"
      );
    } else {
      console.error(
        "Erro:",
        err.message
      );
    }

    alert(
      "Não foi possível carregar os usuários"
    );
  }
}, [
  carregarUsuarios,
  debouncedSearch,
  page
]);

useEffect(() => {
  fetchUsuarios();
}, [fetchUsuarios]);

  // =========================================================
  // EDITAR USUÁRIO
  // =========================================================

  const editarUsuario = (usuario) => {
    setUsuarioEditando(usuario);

    setForm({
      nome: usuario.nome || "",
      email: usuario.email || "",
      profissao: usuario.profissao || "",
      vinculo: usuario.vinculo || "",
      role: usuario.role || "",
      isAdmin: usuario.isAdmin || false,
      isTI: usuario.isTI || false,
      statusUsuario:
        usuario.statusUsuario ||
        "desabilitado"
    });

    setNovaSenha("");
  };

  // =========================================================
  // ALTERAR FORMULÁRIO
  // =========================================================

  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value
    }));
  };

  // =========================================================
  // SALVAR ALTERAÇÕES
  // =========================================================

  const salvarAlteracoes = async () => {
    
    const confirmar = window.confirm(
      "Deseja realmente salvar as alterações deste usuário?"
    );

    if (!confirmar) {
      return;
    }

    try {
      await atualizarUsuario(
        usuarioEditando._id,
        form
      );

      alert(
        "Usuário atualizado com sucesso!"
      );

      setUsuarioEditando(null);

      await fetchUsuarios();
    } catch (error) {
      console.error(
        "Erro ao atualizar usuário:",
        error
      );

      alert(
        error.response?.data?.erro ||
        "Erro ao atualizar usuário."
      );
    }
  };

  // =========================================================
  // RESETAR SENHA
  // =========================================================

  const handleResetSenha = async () => {
    if (!novaSenha) {
      return alert(
        "Digite a nova senha"
      );
    }

    const confirmar = window.confirm(
      "Deseja realmente resetar a senha deste usuário?"
    );

    if (!confirmar) {
      return;
    }

    try {
      await resetSenha(
        usuarioEditando._id,
        novaSenha
      );

      alert("Senha resetada!");

      setNovaSenha("");
    } catch (error) {
      console.error(
        "Erro ao resetar senha:",
        error
      );

      alert(
        error.response?.data?.erro ||
        "Erro ao resetar senha."
      );
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h1>GERENCIAMENTO DE USUÁRIOS</h1>
        <hr />

        {/* =================================================
            BUSCA
        ================================================= */}

        <div className="form-group">
          <label>Buscar usuários</label>

          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
          />
        </div>

        {/* =================================================
            TABELA
        ================================================= */}

        <table className="table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Profissão</th>
              <th>Vínculo</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>

          <tbody>
            {usuarios.map((usuario) => (
              <tr key={usuario._id}>
                <td>
                  {formatarNomeUpper(
                    usuario.nome
                  )}
                </td>

                <td>
                  {usuario.email}
                </td>

                <td>
                  {usuario.profissao}
                </td>

                <td>
                  {usuario.vinculo}
                </td>

                <td>
                  {usuario.statusUsuario}
                </td>

                <td>
                  <button
                    onClick={() =>
                      editarUsuario(usuario)
                    }
                  >
                    Editar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* =================================================
            PAGINAÇÃO
        ================================================= */}

        <div className="pagination">
          <button
            disabled={page === 1}
            onClick={() =>
              setPage((p) => p - 1)
            }
          >
            ⬅ Anterior
          </button>

          <span>
            Página {page} de {totalPages}
          </span>

          <button
            disabled={
              page === totalPages
            }
            onClick={() =>
              setPage((p) => p + 1)
            }
          >
            Próxima ➡
          </button>
        </div>
      </div>

      {/* ===================================================
          MODAL
      =================================================== */}

      {usuarioEditando && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>EDITAR USUÁRIO</h3>

            <hr />

            {/* =============================================
                NOME
            ============================================= */}

            <div className="form-group">
              <label>Nome</label>

              <input
                name="nome"
                value={form.nome}
                onChange={handleChange}
              />
            </div>

            {/* EMAIL (3. CAMPO DE EMAIL ADICIONADO NO FORM) */}
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
            </div>

            {/* =============================================
                PROFISSÃO
            ============================================= */}

            <div className="form-group">
              <label>Profissão</label>

              <select
                name="profissao"
                value={form.profissao}
                onChange={handleChange}
              >
                <option value="">
                  Selecione
                </option>

                <option value="advogado">
                  Advogado
                </option>

                <option value="assessor_juridico">
                  Assessor Jurídico
                </option>

                <option value="estagiario">
                  Estagiário
                </option>

                <option value="recepcionista">
                  Recepcionista
                </option>
              </select>
            </div>

            {/* =============================================
                VÍNCULO
            ============================================= */}

            <div className="form-group">
              <label>Vínculo</label>

              <select
                name="vinculo"
                value={form.vinculo}
                onChange={handleChange}
              >
                <option value="">
                  Selecione
                </option>

                <option value="interno">
                  Interno
                </option>

                <option value="parceiro">
                  Parceiro
                </option>
              </select>
            </div>

            {/* =============================================
                PERFIL
            ============================================= */}

            <div className="form-group">
              <label>Perfil</label>

              <select
                name="role"
                value={form.role}
                onChange={handleChange}
              >
                <option value="">
                  Selecione
                </option>

                <option value="advogado_admin">
                  Advogado administrador
                </option>

                <option value="advogado">
                  Advogado
                </option>

                <option value="advogado_parceiro">
                  Advogado parceiro
                </option>

                <option value="advogado_admin">
                  Advogado administrador
                </option>

                <option value="assessor_juridico">
                  Assessor jurídico
                </option>

                <option value="estagiario">
                  Estagiario
                </option>

                <option value="recepcionista">
                  Recepcionista
                </option>

              </select>
            </div>

            {/* =============================================
                ADMIN
            ============================================= */}

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="isAdmin"
                  checked={form.isAdmin}
                  onChange={handleChange}
                />

                Admin
              </label>
            </div>

            {/* =============================================
                TI
            ============================================= */}

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="isTI"
                  checked={form.isTI}
                  onChange={handleChange}
                />

                TI
              </label>
            </div>

            {/* =============================================
                STATUS
            ============================================= */}

            <div className="form-group">
              <label>
                Status do Usuário
              </label>

              <select
                name="statusUsuario"
                value={
                  form.statusUsuario ||
                  "desabilitado"
                }
                onChange={handleChange}
              >
                <option value="desabilitado">
                  Desabilitado
                </option>

                <option value="habilitado">
                  Habilitado
                </option>
              </select>
            </div>

            <hr />

            {/* =============================================
                RESET DE SENHA
            ============================================= */}

            <h4>Resetar Senha</h4>

            <div className="form-group">
              <input
                type="password"
                placeholder="Nova senha"
                value={novaSenha}
                onChange={(e) =>
                  setNovaSenha(
                    e.target.value
                  )
                }
              />
            </div>

            <button
              onClick={handleResetSenha}
            >
              Resetar Senha
            </button>

            <hr />

            {/* =============================================
                BOTÕES
            ============================================= */}

            <div className="form-buttons">
              <button
                onClick={
                  salvarAlteracoes
                }
              >
                Salvar
              </button>

              <button
                onClick={() => {
                  setUsuarioEditando(null);
                  setNovaSenha("");
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GerenciamentoUsuarios;